import { computed, inject, Injectable, signal } from '@angular/core';
import { AdminServiceV2, SidebarBadgesResponseV2 } from 'api-client';
import { catchError, of } from 'rxjs';
import { LogService } from 'shared';

/**
 * Store for sidebar badge counts.
 * Provides signals for the number of transactions and users in each state:
 *
 * Transaction badges:
 * - Failed: Payment failed (returnCode not '00')
 * - To Transfer: Paid but not yet transferred to branch
 * - Transferred: Transferred to branch but not paid to establishment
 * - Paid (Settled): Paid to establishment
 * - Cancel Requests: Pending cancellation requests
 *
 * User badges:
 * - Admin Users: Count of admin/superadmin users
 * - Customer Users: Count of customer users
 * - App Users: Count of mobile app users
 */
@Injectable({ providedIn: 'root' })
export class TransactionCountsStore {
  private readonly adminService = inject(AdminServiceV2);
  private readonly logger = inject(LogService);

  // Private writable signals - Transaction badges
  private readonly _allTransactionsCount = signal(0);
  private readonly _failedCount = signal(0);
  private readonly _toTransferCount = signal(0);
  private readonly _transferredCount = signal(0);
  private readonly _paidCount = signal(0);
  private readonly _cancelRequestsCount = signal(0);

  // Private writable signals - User badges
  private readonly _allUsersCount = signal(0);
  private readonly _adminUsersCount = signal(0);
  private readonly _customerUsersCount = signal(0);
  private readonly _appUsersCount = signal(0);
  private readonly _rolesCount = signal(0);
  private readonly _permissionsCount = signal(0);

  // Private writable signals - Establishment badges
  private readonly _establishmentsCount = signal(0);

  // Private writable signals - Payment Link badges
  private readonly _paymentLinksCount = signal(0);

  // Private writable signals - State
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _lastRefresh = signal<Date | null>(null);

  // Public readonly signals - Transaction badges
  readonly allTransactionsCount = this._allTransactionsCount.asReadonly();
  readonly failedCount = this._failedCount.asReadonly();
  readonly toTransferCount = this._toTransferCount.asReadonly();
  readonly transferredCount = this._transferredCount.asReadonly();
  readonly paidCount = this._paidCount.asReadonly();
  readonly cancelRequestsCount = this._cancelRequestsCount.asReadonly();

  // Public readonly signals - User badges
  readonly allUsersCount = this._allUsersCount.asReadonly();
  readonly adminUsersCount = this._adminUsersCount.asReadonly();
  readonly customerUsersCount = this._customerUsersCount.asReadonly();
  readonly appUsersCount = this._appUsersCount.asReadonly();
  readonly rolesCount = this._rolesCount.asReadonly();
  readonly permissionsCount = this._permissionsCount.asReadonly();

  // Public readonly signals - Establishment badges
  readonly establishmentsCount = this._establishmentsCount.asReadonly();

  // Public readonly signals - Payment Link badges
  readonly paymentLinksCount = this._paymentLinksCount.asReadonly();

  // Public readonly signals - State
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly lastRefresh = this._lastRefresh.asReadonly();

  // Computed: total urgent (failed + cancel requests)
  readonly urgentCount = computed(() => this._failedCount() + this._cancelRequestsCount());

  /**
   * Refresh all counts from API.
   * Fetches sidebar badge counts from the AdminServiceV2 endpoint.
   */
  refreshCounts(): void {
    this._loading.set(true);
    this._error.set(null);

    this.adminService
      .apiV2AdminSidebarBadgesGet()
      .pipe(
        catchError((err) => {
          this.logger.error('Failed to fetch sidebar badges', 'TransactionCountsStore', err);
          this._error.set('Failed to load transaction counts');
          this._loading.set(false);
          return of(null);
        }),
      )
      .subscribe((response: SidebarBadgesResponseV2 | null) => {
        if (!response) return;

        // Transaction badges
        const txBadges = response.transactions ?? {};
        this._allTransactionsCount.set(txBadges.all ?? 0);
        this._failedCount.set(txBadges.failed ?? 0);
        this._toTransferCount.set(txBadges.toTransfer ?? 0);
        this._transferredCount.set(txBadges.transferred ?? 0);
        this._paidCount.set(txBadges.paid ?? 0);
        this._cancelRequestsCount.set(txBadges.cancelRequests ?? 0);

        // User badges
        const userBadges = response.users ?? {};
        this._allUsersCount.set(userBadges.all ?? 0);
        this._adminUsersCount.set(userBadges.adminUsers ?? 0);
        this._customerUsersCount.set(userBadges.customerUsers ?? 0);
        this._appUsersCount.set(userBadges.appUsers ?? 0);
        this._rolesCount.set(userBadges.roles ?? 0);
        this._permissionsCount.set(userBadges.permissions ?? 0);

        // Establishment badges
        const establishmentBadges = response.establishments ?? {};
        this._establishmentsCount.set(establishmentBadges.all ?? 0);

        // Payment Link badges
        const paymentLinkBadges = response.paymentLinks ?? {};
        this._paymentLinksCount.set(paymentLinkBadges.all ?? 0);

        this._loading.set(false);
        this._lastRefresh.set(new Date());
      });
  }

  /**
   * Check if refresh is needed (older than 5 minutes or never refreshed).
   */
  shouldRefresh(): boolean {
    const last = this._lastRefresh();
    if (!last) return true;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    return Date.now() - last.getTime() > FIVE_MINUTES_MS;
  }
}
